import { atom, getDefaultStore } from 'jotai'

import { getWsUrl } from '../lib/config'
import { api } from '../lib/api'
import { AUTH_TOKEN_KEY, SESSION_SNAPSHOT_KEY, removeItem } from '../lib/storage'
import { WebSocketClient } from '../lib/websocket'

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting'

export const connectionStatusAtom = atom<ConnectionStatus>('disconnected')
export const connectionErrorAtom = atom<string | null>(null)

let client: WebSocketClient | null = null
let authToken: string | null = null
let reconnectAttempts = 0
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let connectInFlight = false

export function getClient(): WebSocketClient | null {
  return client
}

export function isSocketOpen(): boolean {
  return Boolean(client?.isOpen())
}

function clearReconnectTimer() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
}

function dropClient() {
  if (!client) return
  try {
    client.disconnect()
  } catch {
    // ignore
  }
  client = null
}

function scheduleReconnect(set: (atom: any, value: any) => void) {
  if (!authToken) return
  clearReconnectTimer()
  reconnectAttempts += 1
  const delayMs = Math.min(30_000, 1000 * 2 ** Math.min(reconnectAttempts - 1, 5))
  set(connectionStatusAtom, 'reconnecting')
  const tokenToUse = authToken
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    if (authToken !== tokenToUse) return
    dropClient()
    connectInFlight = false
    getDefaultStore().set(connectAtom, tokenToUse)
  }, delayMs)
}

async function clearLocalSessionOnReject() {
  api.clearAuthToken()
  removeItem(AUTH_TOKEN_KEY)
  removeItem(SESSION_SNAPSHOT_KEY)
  const session = await import('./session')
  session.markUnauthenticated()
}

export const connectAtom = atom(null, async function connect(_get, set, token: string) {
  authToken = token

  if (client?.isOpen()) {
    set(connectionStatusAtom, 'connected')
    set(connectionErrorAtom, null)
    reconnectAttempts = 0
    clearReconnectTimer()
    return
  }

  if (connectInFlight) return
  connectInFlight = true

  dropClient()
  clearReconnectTimer()
  set(connectionStatusAtom, 'connecting')
  set(connectionErrorAtom, null)

  const wsUrl = getWsUrl()
  client = new WebSocketClient(wsUrl)

  client.onClose((event) => {
    set(connectionStatusAtom, 'disconnected')

    if (event.code === 1008) {
      console.log('User rejected by server - clearing session')
      void clearLocalSessionOnReject()
      set(connectionErrorAtom, 'Session terminated')
      dropClient()
      authToken = null
      clearReconnectTimer()
      return
    }

    if (authToken) scheduleReconnect(set)
  })

  try {
    await client.connect(token)
    if (!client?.isOpen()) throw new Error('WebSocket closed during connect')
    reconnectAttempts = 0
    clearReconnectTimer()
    set(connectionStatusAtom, 'connected')
    set(connectionErrorAtom, null)
  } catch (err) {
    console.error('[websocket] connect failed:', wsUrl, err)
    set(connectionStatusAtom, 'disconnected')
    set(connectionErrorAtom, err instanceof Error ? err.message : 'Failed to connect')
    dropClient()
    if (authToken) scheduleReconnect(set)
  } finally {
    connectInFlight = false
  }
})

/** Verify real socket; reconnect immediately (tab focus / visibility). */
export const ensureConnectedAtom = atom(
  null,
  async function ensureConnected(_get, set, token: string) {
    authToken = token
    if (client?.isOpen()) {
      set(connectionStatusAtom, 'connected')
      set(connectionErrorAtom, null)
      reconnectAttempts = 0
      clearReconnectTimer()
      return
    }
    clearReconnectTimer()
    reconnectAttempts = 0
    connectInFlight = false
    dropClient()
    await set(connectAtom, token)
  },
)

export const disconnectAtom = atom(null, function disconnect(_get, set) {
  clearReconnectTimer()
  reconnectAttempts = 0
  authToken = null
  connectInFlight = false
  dropClient()
  set(connectionStatusAtom, 'disconnected')
  set(connectionErrorAtom, null)
})
